import React from "react";
import { Button, ButtonVariants, Devices } from "@app/shared";
import { NavLink } from "react-router-dom";
import { Icons } from "../../../../shared";

interface SetupDeviceInRealWorldStepProps {
    facilityName?: string;
    groupName: string;
    deviceId: string;
    onNext: () => void;
    onBack: () => Promise<void>;
}

export const SetupDeviceInRealWorldStep: React.FC<SetupDeviceInRealWorldStepProps> = ({ facilityName, groupName, deviceId, onNext, onBack }) => {
    const handleDissasociate = async () => {
        try {
            await Devices.disassociateDevice(deviceId);
            await onBack();
        } catch (error) {
            console.log(error);
        }
    };

    return (
        <div className="max-w-2xl mx-auto">
            <div className="mb-8 text-center">
                <div className="w-16 h-16 bg-gradient-to-br from-brand-primary to-brand-primary-darkened rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                    <Icons.HardwareChipOutline className="w-8 h-8 text-white" />
                </div>
                <h2 className="text-2xl font-bold text-neutral-900 mb-2">Install Your Device</h2>
                <p className="text-neutral-600 mb-4">Now it's time to install it at the physical location.</p>
                <div className="flex flex-col sm:flex-row gap-2 justify-center">
                    <div className="inline-flex items-center px-4 py-2 bg-white border border-warmGray-200 rounded-full text-sm shadow-sm">
                        <div className="w-2 h-2 bg-brand-primary rounded-full mr-2"></div>
                        <span className="text-neutral-700 font-medium">Group: {groupName}</span>
                    </div>
                    {facilityName && (
                        <div className="inline-flex items-center px-4 py-2 bg-white border border-warmGray-200 rounded-full text-sm shadow-sm">
                            <div className="w-2 h-2 bg-brand-primary rounded-full mr-2"></div>
                            <span className="text-neutral-700 font-medium">Location: {facilityName}</span>
                        </div>
                    )}
                </div>
            </div>

            <div className="bg-white rounded-xl shadow-lg border border-warmGray-100 p-8 mb-8">
                <h3 className="text-lg font-semibold text-neutral-900 mb-6 flex items-center">
                    <div className="w-6 h-6 bg-brand-primary rounded-lg flex items-center justify-center mr-3">
                        <Icons.LocationOutline className="w-4 h-4 text-white" />
                    </div>
                    Installation Steps
                </h3>
                <div className="space-y-6">
                    <div className="flex items-start">
                        <div className="w-10 h-10 bg-gradient-to-br from-brand-primary to-brand-primary-darkened rounded-xl flex items-center justify-center mr-4 mt-0.5 flex-shrink-0 shadow-md">
                            <span className="text-white font-bold text-sm">1</span>
                        </div>
                        <div>
                            <h4 className="font-semibold text-neutral-900 mb-2">Locate the Installation Site</h4>
                            <p className="text-sm text-neutral-600 leading-relaxed">Go to your designated location and identify the optimal mounting position for your device.</p>
                        </div>
                    </div>

                    <div className="flex items-start">
                        <div className="w-10 h-10 bg-gradient-to-br from-brand-primary to-brand-primary-darkened rounded-xl flex items-center justify-center mr-4 mt-0.5 flex-shrink-0 shadow-md">
                            <span className="text-white font-bold text-sm">2</span>
                        </div>
                        <div>
                            <h4 className="font-semibold text-neutral-900 mb-2">Mount the Device</h4>
                            <p className="text-sm text-neutral-600 leading-relaxed">Securely mount the device according to the installation guide. Ensure proper positioning for optimal coverage.</p>
                        </div>
                    </div>

                    <div className="flex items-start">
                        <div className="w-10 h-10 bg-gradient-to-br from-brand-primary to-brand-primary-darkened rounded-xl flex items-center justify-center mr-4 mt-0.5 flex-shrink-0 shadow-md">
                            <span className="text-white font-bold text-sm">3</span>
                        </div>
                        <div>
                            <h4 className="font-semibold text-neutral-900 mb-2">Connect Power & Network</h4>
                            <p className="text-sm text-neutral-600 leading-relaxed">Connect the device to power and ensure network connectivity. Wait for the device to come online.</p>
                        </div>
                    </div>
                </div>
            </div>

            <div className="text-center mb-4">
                <NavLink to="https://hubs.ly/Q03qFVXm0" target="_blank" className="inline-flex items-center gap-3 px-8 py-4 rounded-xl transition-all duration-200 font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5">
                    <Icons.DocumentText className="w-5 h-5" />
                    View Setup Guide
                </NavLink>
            </div>

            <div className="bg-gradient-to-r from-warmGray-50 to-white border border-warmGray-100 rounded-xl p-6 mb-8">
                <div className="flex items-start">
                    <div className="w-8 h-8 bg-brand-primary rounded-lg flex items-center justify-center mr-3 flex-shrink-0 mt-0.5">
                        <Icons.Info className="w-4 h-4 text-white" />
                    </div>
                    <div>
                        <h4 className="font-semibold text-neutral-900 mb-3">What's Next?</h4>
                        <p className="text-sm text-neutral-600 leading-relaxed">Once your device is physically installed and online, return here to continue with the setup process. You'll be able to map the device location and configure monitoring areas.</p>
                    </div>
                </div>
            </div>

            <div className="flex justify-between gap-4 mb-8">
                <Button variant={ButtonVariants.Secondary} onClick={handleDissasociate} className="flex-1 flex justify-center items-center bg-white border border-warmGray-300 hover:border-warmGray-400 text-neutral-700 font-semibold rounded-xl transition-all duration-200 hover:bg-warmGray-50">
                    <Icons.ArrowBack className="w-4 h-4 mr-2" />
                    Change Location
                </Button>
                <Button onClick={onNext} className="flex-1 flex justify-center items-center bg-brand-primary hover:bg-brand-primary-darkened text-white font-semibold rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl">
                    <Icons.Success className="w-4 h-4 mr-2" />
                    Device is Installed - Continue
                </Button>
            </div>

            <div className="bg-gradient-to-r from-terracotta-50 to-warmGray-50 border border-brand-primary border-opacity-20 rounded-xl p-6">
                <div className="flex items-start">
                    <div className="w-8 h-8 bg-brand-primary rounded-lg flex items-center justify-center mr-3 flex-shrink-0 mt-0.5">
                        <Icons.QuestionMarkCircleOutline className="w-4 h-4 text-white" />
                    </div>
                    <div>
                        <h4 className="font-semibold text-neutral-900 mb-3">Need Help?</h4>
                        <div className="text-sm text-neutral-600 space-y-3 leading-relaxed">
                            <p>
                                <span className="font-medium text-neutral-700">Installation issues?</span> Check our installation guide or contact technical support for assistance.
                            </p>
                            <p>
                                <span className="font-medium text-neutral-700">Device not connecting?</span> Verify network settings and ensure the device has proper power supply.
                            </p>
                            <p>
                                <span className="font-medium text-neutral-700">Questions about positioning?</span> Contact our support team for site-specific recommendations.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
